import { useMutation, useQueryClient } from '@tanstack/react-query'
import { functionalUpdate } from '@tanstack/react-table'
import { debounce, isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../context'
import { mutation } from '../graphql'
import { normalizeMeta } from '../helpers/employee'
import {
  EMPTY_DRAFT,
  TABLE_SETTINGS_META_ID,
  buildEntry,
  getActiveIds,
  getColumnRegistry,
  isDefaultSettings,
  normalizeStored,
  orderCustomizable,
  readTableSettings,
  renormalizeDraft,
  resolveColumnOrder,
  toggleOptional,
  upsertMetaEntry,
  withOrder
} from '../helpers/table-column-settings'

const PERSIST_DEBOUNCE_MS = 800
const PERSIST_MAX_WAIT_MS = 3000

/**
 * Spalten-Einstellungen (Sichtbarkeit, Reihenfolge) pro User und Tabelle.
 *
 * Der lokale Draft ist die einzige Quelle für die Tabelle, damit ein fremder
 * refetchActiveSpace (Stern-Klick, Hints) die Ansicht nie zurücksetzt.
 * Geschrieben wird debounced in Space.customer_dashboard_meta: erst der
 * react-query-Cache (optimistisch, damit alle anderen Meta-Writer den neuen
 * Eintrag sofort sehen), dann updateSpace. Die Mutation-Response wird nicht
 * in den Cache geschrieben, sie könnte einen neueren Draft überschreiben.
 *
 * Vererbung: partnerweit deaktivierte Spalten (Admin-Einstellung unter
 * Dashboard-Komponenten) sind für den Nutzer weder sichtbar noch wählbar.
 * Seine Präferenzen dazu bleiben im Eintrag erhalten und greifen wieder,
 * sobald der Admin die Spalte freigibt.
 *
 * Optionale Spalten (meta.optional, die Zusatzfelder) sind ausgeblendet, bis der
 * Nutzer sie hinzufügt. Sie stehen dann in items, vorher in additionalItems.
 * Reihenfolge und Persistenz laufen nur über die aktiven Ids, damit nicht
 * gezeigte Zusatzfelder weder in order landen noch als Anpassung zählen.
 */
export const useTableColumnSettings = ({ tableId, columns }) => {
  const { t } = useTranslation()
  const { activeSpace, refetchActiveSpace } = useUserContext()
  const { partner } = usePartnerContext()
  const { error } = useNotificationContext()
  const queryClient = useQueryClient()

  const registry = useMemo(() => getColumnRegistry(columns), [columns])
  const { customizableIds, optionalIds, primary, labelsById } = registry

  const disabledKey = (partner.disabledTableColumns?.[tableId] || []).join('|')
  const disabledSet = useMemo(
    () => new Set(disabledKey ? disabledKey.split('|') : []),
    [disabledKey]
  )

  // Für den Nutzer anpassbar ist nur, was der Partner nicht deaktiviert hat.
  const effectiveIds = useMemo(
    () => customizableIds.filter((id) => !disabledSet.has(id)),
    [customizableIds, disabledSet]
  )

  const disabledItems = useMemo(
    () =>
      customizableIds
        .filter((id) => disabledSet.has(id))
        .map((id) => ({ id, label: labelsById[id] || id })),
    [customizableIds, disabledSet, labelsById]
  )

  // activeSpace.customer_dashboard_meta ist im UserContext bereits ein Array,
  // normalizeMeta ist hier nur die Absicherung gegen krumme Altdaten.
  const stored = useMemo(
    () =>
      readTableSettings(
        normalizeMeta(activeSpace.customer_dashboard_meta),
        tableId
      ),
    [activeSpace.customer_dashboard_meta, tableId]
  )

  const [draft, setDraft] = useState(() =>
    normalizeStored(stored, effectiveIds)
  )

  const latestDraftRef = useRef(draft)
  const pendingRef = useRef(false)
  // Zähler statt Boolean: überlappende Writes (maxWait, langsames Netz) dürfen
  // den Re-Sync erst freigeben, wenn der letzte gesettelt ist.
  const inFlightRef = useRef(0)
  const mountedRef = useRef(true)
  const activeSpaceIdRef = useRef(activeSpace.id)
  const effectiveIdsRef = useRef(effectiveIds)
  const optionalIdsRef = useRef(optionalIds)
  const columnsRef = useRef(columns)
  const tableIdRef = useRef(tableId)

  activeSpaceIdRef.current = activeSpace.id
  effectiveIdsRef.current = effectiveIds
  optionalIdsRef.current = optionalIds
  columnsRef.current = columns
  tableIdRef.current = tableId

  // Re-Sync: Änderungen aus dem Cache übernehmen (anderer Tab, Refetch),
  // aber nie, solange ein eigener Write pending oder in flight ist. Ändert
  // sich die Spaltenmenge (bedingte Spalten), wird der Draft neu normalisiert.
  useEffect(() => {
    const busy = pendingRef.current || inFlightRef.current > 0
    const next = busy
      ? renormalizeDraft(latestDraftRef.current, effectiveIds)
      : normalizeStored(stored, effectiveIds)

    if (isEqual(latestDraftRef.current, next)) return

    latestDraftRef.current = next
    setDraft(next)
  }, [stored, effectiveIds])

  const buildMeta = useCallback((prev) => {
    const meta = normalizeMeta(prev?.customer_dashboard_meta)
    const draftValue = latestDraftRef.current
    const entry = buildEntry(
      meta,
      tableIdRef.current,
      draftValue,
      getActiveIds(draftValue, effectiveIdsRef.current, optionalIdsRef.current)
    )

    return JSON.stringify(upsertMetaEntry(meta, TABLE_SETTINGS_META_ID, entry))
  }, [])

  const mergeDraftIntoCache = useCallback(() => {
    const id = activeSpaceIdRef.current
    if (!id) return

    queryClient.setQueryData(QUERY_KEYS.activeSpaceById(id), (prev) =>
      prev ? { ...prev, customer_dashboard_meta: buildMeta(prev) } : prev
    )
  }, [queryClient, buildMeta])

  const { mutate } = useMutation({
    mutationFn: ({ input }) => mutation({ mutation: 'updateSpace', input }),
    onSuccess: () => {
      // Nach dem Unmount ist latestDraftRef eingefroren und eine neuere Instanz
      // derselben Tabelle besitzt den Cache. Der optimistische Write aus flush
      // liegt bereits drin, hier nichts mehr mergen.
      if (!mountedRef.current) return

      // Heilt den Fall, dass ein fremder Refetch während des In-Flight alte
      // Server-Daten in den Cache geschrieben hat.
      mergeDraftIntoCache()
    },
    onError: () => {
      error(t('react_table.column_settings.save_error'))
      refetchActiveSpace()
    },
    onSettled: () => {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1)
    }
  })

  const flushRef = useRef(() => undefined)
  flushRef.current = () => {
    const id = activeSpaceIdRef.current
    if (!id) return

    const key = QUERY_KEYS.activeSpaceById(id)
    const cdm = buildMeta(queryClient.getQueryData(key))

    // Laufenden getSpace verwerfen: seine späte Antwort würde den
    // optimistischen Eintrag überschreiben. refetch() der Aufrufer resolved
    // trotzdem, react-query schluckt den CancelledError dort.
    queryClient.cancelQueries({ queryKey: key })

    queryClient.setQueryData(key, (prev) =>
      prev ? { ...prev, customer_dashboard_meta: cdm } : prev
    )

    pendingRef.current = false
    inFlightRef.current += 1
    mutate({ input: { id, customer_dashboard_meta: cdm } })
  }

  const persist = useMemo(
    () =>
      debounce(() => flushRef.current(), PERSIST_DEBOUNCE_MS, {
        trailing: true,
        maxWait: PERSIST_MAX_WAIT_MS
      }),
    []
  )

  // Offene Änderungen beim Verlassen der Seite und beim Unmount schreiben.
  useEffect(() => {
    mountedRef.current = true
    const onPageHide = () => persist.flush()
    window.addEventListener('pagehide', onPageHide)

    return () => {
      mountedRef.current = false
      window.removeEventListener('pagehide', onPageHide)
      persist.flush()
    }
  }, [persist])

  const commit = useCallback(
    (next) => {
      latestDraftRef.current = next
      pendingRef.current = true
      setDraft(next)
      persist()
    },
    [persist]
  )

  const toggleColumn = useCallback(
    (id) => {
      const current = latestDraftRef.current

      if (optionalIdsRef.current.includes(id)) {
        commit(
          toggleOptional(
            current,
            id,
            effectiveIdsRef.current,
            optionalIdsRef.current
          )
        )
        return
      }

      const hidden = current.hidden.includes(id)
        ? current.hidden.filter((hiddenId) => hiddenId !== id)
        : [...current.hidden, id]

      commit({ ...current, hidden })
    },
    [commit]
  )

  const reorderColumns = useCallback(
    (ids) => {
      const current = latestDraftRef.current
      commit(
        withOrder(
          current,
          ids,
          getActiveIds(current, effectiveIdsRef.current, optionalIdsRef.current)
        )
      )
    },
    [commit]
  )

  // delta: Zahl (relativ) oder 'first' | 'last'. Liefert { from, to } oder null.
  const moveColumn = useCallback(
    (id, delta) => {
      const active = getActiveIds(
        latestDraftRef.current,
        effectiveIdsRef.current,
        optionalIdsRef.current
      )
      const current = orderCustomizable(latestDraftRef.current.order, active)
      const from = current.indexOf(id)
      if (from < 0) return null

      const last = current.length - 1
      let to
      if (delta === 'first') to = 0
      else if (delta === 'last') to = last
      else to = Math.min(Math.max(from + delta, 0), last)

      if (to === from) return null

      const order = [...current]
      order.splice(from, 1)
      order.splice(to, 0, id)

      commit(withOrder(latestDraftRef.current, order, active))

      return { from, to }
    },
    [commit]
  )

  // Nur ausgeblendete reguläre Spalten, Zusatzfelder bleiben, wie sie sind.
  const showAll = useCallback(() => {
    commit({ ...latestDraftRef.current, hidden: [], hiddenUnknown: [] })
  }, [commit])

  const reset = useCallback(() => {
    commit(EMPTY_DRAFT)
  }, [commit])

  // react-table ruft die Setter mit Wert oder Updater auf.
  const setColumnVisibility = useCallback(
    (updater) => {
      const draftValue = latestDraftRef.current
      const optional = new Set(optionalIdsRef.current)
      const current = Object.fromEntries([
        ...draftValue.hidden.map((id) => [id, false]),
        ...optionalIdsRef.current
          .filter((id) => !draftValue.shown.includes(id))
          .map((id) => [id, false])
      ])
      const next = functionalUpdate(updater, current)
      const known = new Set(effectiveIdsRef.current)
      const hidden = Object.entries(next)
        .filter(
          ([id, visible]) =>
            visible === false && known.has(id) && !optional.has(id)
        )
        .map(([id]) => id)
      // Ein Zusatzfeld kommt nur mit explizitem true dazu, sonst würde ein
      // Reset der Sichtbarkeit alle Felder auf einmal einblenden.
      const shown = optionalIdsRef.current.filter((id) =>
        draftValue.shown.includes(id) ? next[id] !== false : next[id] === true
      )

      commit({ ...draftValue, hidden, shown })
    },
    [commit]
  )

  const setColumnOrder = useCallback(
    (updater) => {
      const current = resolveColumnOrder(
        columnsRef.current,
        latestDraftRef.current.order
      )

      reorderColumns(functionalUpdate(updater, current))
    },
    [reorderColumns]
  )

  const columnOrder = useMemo(
    () => resolveColumnOrder(columns, draft.order),
    [columns, draft.order]
  )

  const activeIds = useMemo(
    () => getActiveIds(draft, effectiveIds, optionalIds),
    [draft, effectiveIds, optionalIds]
  )

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        [
          ...draft.hidden,
          ...disabledItems.map((item) => item.id),
          ...optionalIds.filter((id) => !draft.shown.includes(id))
        ].map((id) => [id, false])
      ),
    [draft.hidden, draft.shown, disabledItems, optionalIds]
  )

  const columnState = useMemo(
    () => ({
      columnVisibility,
      columnOrder,
      setColumnVisibility,
      setColumnOrder
    }),
    [columnVisibility, columnOrder, setColumnVisibility, setColumnOrder]
  )

  const items = useMemo(
    () =>
      orderCustomizable(draft.order, activeIds).map((id) => ({
        id,
        label: labelsById[id] || id,
        visible: !draft.hidden.includes(id)
      })),
    [draft.order, draft.hidden, activeIds, labelsById]
  )

  const additionalItems = useMemo(
    () =>
      optionalIds
        .filter((id) => !draft.shown.includes(id))
        .map((id) => ({ id, label: labelsById[id] || id })),
    [optionalIds, draft.shown, labelsById]
  )

  return {
    columnState,
    items,
    additionalItems,
    disabledItems,
    primary,
    hiddenCount: draft.hidden.length,
    isCustomized: !isDefaultSettings(draft, activeIds),
    toggleColumn,
    reorderColumns,
    moveColumn,
    showAll,
    reset
  }
}
